import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import UserAvatar, {
  BasicPerson,
} from '@/components/molecules/user-avatar/user-avatar';
import styles from './room-occupants-overview.module.scss';

export interface RoomOccupantsOverviewProps {
  /** The class name to apply */
  className?: string;
  /** Indicates whether the component is fetching data */
  isFetching?: boolean;
  /** Indicates whether the component is in a loading state */
  isLoading?: boolean;
  /** The passengers of the room */
  passengers?: Content.Properties.Passenger[];
  /** The name of the room */
  roomName?: string;
  /** The number of the room */
  roomNumber?: number;
}

const RoomOccupantsOverview = ({
  className,
  isFetching = false,
  isLoading = false,
  passengers,
  roomName,
  roomNumber,
}: RoomOccupantsOverviewProps) => {
  const { t } = useI18n();

  const getBasicPerson = (
    passenger: Content.Properties.Passenger,
  ): BasicPerson => {
    const { firstName, lastName, placeholders } = passenger;

    if (!firstName && !lastName) {
      return {
        firstName: placeholders.firstName,
        lastName: placeholders.lastName,
      };
    }

    return { firstName, lastName };
  };

  const getNameDisplay = (passenger: Content.Properties.Passenger): string => {
    const { firstName, lastName, placeholders } = passenger;

    if (!firstName && !lastName) {
      return `${placeholders.firstName} ${placeholders.lastName}`;
    }

    return `${firstName} ${lastName}`;
  };

  if (isLoading) {
    return (
      <div
        className={classNames(styles.component, className, styles.isLoading)}
      >
        <div className={styles.headlineWrapper}>
          <div className={styles.headline}>
            <Icon name="Bed" size={24} />

            <SkeletonLoader
              className={styles.loader}
              height="24px"
              width="60px"
            />
          </div>

          <SkeletonLoader
            className={classNames(styles.roomName, styles.loader)}
            height="24px"
            width="200px"
          />
        </div>

        <div className={styles.passengers}>
          {Array.from({ length: 3 }).map((_, passengerIndex) => (
            <div key={passengerIndex} className={styles.passenger}>
              <SkeletonLoader borderRadius="999px" height="32px" width="32px" />

              <SkeletonLoader
                className={styles.loader}
                height="22px"
                width="120px"
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.component, className)}>
      <div className={styles.headlineWrapper}>
        <div className={styles.headline}>
          <Icon name="Bed" size={24} />

          {roomNumber && (
            <Typography tagStyle="bodyMediumBold">
              {`${t('items.modules.bookingFlow.room')} ${roomNumber}:`}
            </Typography>
          )}
        </div>

        {roomName && (
          <Typography className={styles.roomName} tagStyle="bodyMedium">
            {roomName}
          </Typography>
        )}
      </div>

      {(isFetching || passengers) && (
        <div className={styles.passengers}>
          {isFetching
            ? Array.from({ length: passengers?.length ?? 3 }).map(
                (_, passengerIndex) => (
                  <div key={passengerIndex} className={styles.passenger}>
                    <SkeletonLoader
                      borderRadius="999px"
                      height="32px"
                      width="32px"
                    />

                    <SkeletonLoader
                      className={styles.loader}
                      height="24px"
                      width="120px"
                    />
                  </div>
                ),
              )
            : passengers &&
              passengers.map((passenger, passengerIndex) => (
                <div key={passengerIndex} className={styles.passenger}>
                  <UserAvatar passenger={getBasicPerson(passenger)} />

                  <Typography className={styles.name} tagStyle="bodyMedium">
                    {getNameDisplay(passenger)}
                  </Typography>
                </div>
              ))}
        </div>
      )}
    </div>
  );
};

RoomOccupantsOverview.displayName = 'RoomOccupantsOverview';

export default RoomOccupantsOverview;
